import { withMeta, type SalonStockerHttpTypes } from "@repo/api-sdk";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { productsPricingQuery } from "./productsPricing";

// The SDK method's own doc comment caps a single `resolvePricing` call at 15
// product ids (`packages/api-sdk/src/sdk/product.ts`) - the pricing hook's own
// comment says 20, but that's stale relative to the actual method signature.
const CHUNK_SIZE = 15;

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export interface UseProductComparisonPricingResult {
  products: SalonStockerHttpTypes.StoreProductWithPricing<"meta">[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Fetches pricing (including every seller's offer) for an arbitrary number of
 * products by chunking into batches of `CHUNK_SIZE` and firing one
 * `productsPricingQuery` per chunk, then flattening the results. This exists
 * because the comparison grid needs pricing for every product in the
 * customer's cart at once, which can exceed a single `resolvePricing` call's
 * cap - `useProductsPricing` itself is unmodified and still used everywhere
 * else for the single-call case.
 */
export function useProductComparisonPricing(
  productIds: string[]
): UseProductComparisonPricingResult {
  const chunks = chunk(productIds, CHUNK_SIZE);

  const results = useQueries({
    queries: chunks.map((ids) => ({
      ...productsPricingQuery({ product_ids: ids }),
      select: (data: SalonStockerHttpTypes.StoreProductPricingResponse) =>
        data.products.map(withMeta),
      // The id list is part of the cache key, and it can change for a moment
      // even when the products haven't: switching a supplier can briefly drop
      // a product from the caller's filtered carts until the cart-selection
      // state catches up. Without this the new key has no data, the hook
      // reports `isLoading`, and the caller swaps the whole grid out for a
      // skeleton - remounting the scroll container and throwing away the
      // buyer's scroll position mid-comparison.
      placeholderData: keepPreviousData
    }))
  });

  return {
    products: results.flatMap((r) => r.data ?? []),
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    error: results.find((r) => r.error)?.error ?? null
  };
}
